import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";
import { characters } from "@/lib/fragrance-data";
import { getCharacterImageAlt } from "@/lib/image-alt";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";

export const Route = createFileRoute("/character-archive/")({
  head: () => ({
    meta: [
      { title: "Character Archive — House of Apocrypha" },
      {
        name: "description",
        content:
          "The Character Archive — portraits of the botanicals that recur across House of Apocrypha's natural perfumes.",
      },
      { property: "og:title", content: "Character Archive — House of Apocrypha" },
      {
        property: "og:description",
        content: "Portraits of the botanicals that recur across House of Apocrypha's natural perfumes.",
      },
      { property: "og:url", content: "https://houseofapocrypha.com/character-archive" },
    ],
    links: [{ rel: "canonical", href: "https://houseofapocrypha.com/character-archive" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
            { "@type": "ListItem", position: 2, name: "Character Archive", item: "https://houseofapocrypha.com/character-archive" },
          ],
        }),
      },
    ],
  }),
  component: CharacterArchiveIndex,
});

function CharacterArchiveIndex() {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <main className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
        <nav className="text-sm">
          <Link to="/field-notes" className="text-muted-foreground hover:text-gold">
            Field Notes
          </Link>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <span className="text-parchment">Character Archive</span>
        </nav>

        <div className="mt-10 text-center">
          <p className="eyebrow">Notes as Personas</p>
          <h1 className="mt-4 text-4xl text-parchment sm:text-5xl">
            <span className="italic text-gold/90">Character</span> Archive
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            Each note has a face — a season, a temper, a story. Browse the cast that recurs across
            the bottles.
          </p>
          <div className="ornament mt-10">
            <span>✶</span>
          </div>
        </div>

        <ul className="mt-16 grid list-none gap-10 sm:grid-cols-2 lg:grid-cols-3" aria-label="Character portraits">
          {characters.map((c, i) => (
            <motion.li
              key={c.slug}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.06 }}
              className="group flex flex-col"
            >
              <Link
                to="/character-archive/$slug"
                params={{ slug: c.slug }}
                className="overflow-hidden border border-border"
                aria-label={`${c.name} — ${c.epithet}: read the portrait`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <ResponsiveImage
                    src={c.image}
                    alt={getCharacterImageAlt(c)}
                    sizes={SIZES.portrait}
                    pictureClassName="contents"
                    className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  />
                </div>
              </Link>

              <div className="mt-5 text-center">
                <h2 className="font-display text-2xl tracking-[0.12em] text-parchment">{c.name}</h2>
                <p className="mt-1 font-serif italic text-muted-foreground">{c.epithet}</p>
                <Link
                  to="/character-archive/$slug"
                  params={{ slug: c.slug }}
                  aria-label={`Read the portrait of ${c.name}, ${c.epithet}`}
                  className="mt-4 inline-block border-b border-gold/40 pb-1 font-display text-xs uppercase tracking-[0.22em] text-gold hover:border-gold hover:text-gold-soft"
                >
                  Read portrait →
                </Link>

              </div>
            </motion.li>
          ))}
        </ul>

      </main>
      <SiteFooter />
    </div>
  );
}
